import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { environment } from '../environments/environment';

function createMockJwt(): string {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(
    JSON.stringify({
      sub: '1',
      email: 'dev@ditrack.com',
      exp: Math.floor(Date.now() / 1000) + 86400 * 365,
      iat: Math.floor(Date.now() / 1000),
    })
  );
  return `${header}.${payload}.dev-signature`;
}

/**
 * Interceptor that handles two scenarios:
 * 1. devLogin=true: Mocks auth responses for local development
 * 2. devLogin=false: Transforms Ditrack backend responses to match
 *    the format expected by the shared auth library
 *
 * Backend returns: { success, data: { access, refresh, user: {...} } }
 * Auth lib expects: { access, refresh, email, username, roles, ... }
 */
export const devAuthInterceptor: HttpInterceptorFn = (req, next) => {
  // --- Mock mode ---
  if ((environment as Record<string, unknown>)['devLogin']) {
    if (req.method === 'POST' && req.url.includes('/api/auth/login/')) {
      const mockToken = createMockJwt();
      return of(
        new HttpResponse({
          status: 200,
          body: {
            access: mockToken,
            refresh: mockToken,
            email: 'dev@ditrack.com',
            username: 'Dev User',
            roles: ['admin'],
            permissions: [],
            user_id: 1,
            provider_id: 1,
          },
        })
      ).pipe(delay(100));
    }

    if (req.method === 'POST' && req.url.includes('/api/auth/token/refresh/')) {
      return of(new HttpResponse({ status: 200, body: { access: createMockJwt() } })).pipe(
        delay(100)
      );
    }

    if (req.method === 'POST' && req.url.includes('/api/auth/logout/')) {
      return of(new HttpResponse({ status: 200, body: {} })).pipe(delay(100));
    }

    return next(req);
  }

  // --- Real mode: transform login response ---
  if (req.method === 'POST' && req.url.includes('/api/auth/login/')) {
    return next(req).pipe(
      map((event) => {
        if (event instanceof HttpResponse && event.body) {
          const body = event.body as Record<string, unknown>;
          if (body['success'] && body['data']) {
            const data = body['data'] as Record<string, unknown>;
            const { access, refresh } = data;
            const user = data['user'] as Record<string, unknown> | undefined;
            return event.clone({
              body: {
                access,
                refresh,
                email: user?.['email'] || '',
                username: user?.['username'] || user?.['full_name'] || '',
                roles: Array.isArray(user?.['roles']) ? user['roles'] : (user?.['role'] ? [user['role']] : []),
                permissions: [],
                user_id: user?.['id'] || 0,
                provider_id: 0,
              },
            });
          }
        }
        return event;
      })
    );
  }

  return next(req);
};
